import type { ModalState } from '@scalar/components/modal'
import type { WorkspaceStore } from '@scalar/workspace-store/client'
import type { WorkspaceEventBus } from '@scalar/workspace-store/events'
import type { TraversedEntry } from '@scalar/workspace-store/schemas/navigation'
import { isOpenApiDocument } from '@scalar/workspace-store/schemas/type-guards'
import { type Ref, ref } from 'vue'

import type { UseModalSidebarReturn } from '@/v2/features/modal/hooks/use-modal-sidebar'
import { initializeWorkspaceEventHandlers } from '@/v2/workspace-events'

// Frozen so this shared fallback cannot be mutated in place, which would leak a stale selection into
// every future open that falls back to empty. Callers only ever reassign the ref, never mutate it.
const EMPTY_REQUEST_BODY_COMPOSITION_SELECTION: Record<string, number> = Object.freeze({})

export function initializeModalEvents({
  eventBus,
  isSidebarOpen,
  requestBodyCompositionSelection,
  sidebarState,
  modalState,
  store,
}: {
  eventBus: WorkspaceEventBus
  isSidebarOpen: Ref<boolean>
  requestBodyCompositionSelection: Ref<Record<string, number>>
  sidebarState: UseModalSidebarReturn
  modalState: ModalState
  store: WorkspaceStore
}) {
  /** Initialize workspace event handlers */
  initializeWorkspaceEventHandlers({
    eventBus,
    store: ref(store),
    hooks: {
      'operation:create:draft-example': {
        onAfterExecute: ({ documentName, meta: { path, method }, exampleName }): void => {
          // The new example must be in the sidebar before its location can be selected.
          store.buildSidebar(documentName)
          const entry = sidebarState.getEntryByLocation({
            document: documentName,
            path,
            method,
            example: exampleName,
          })
          if (entry) {
            sidebarState.handleSelectItem(entry.id)
          }
        },
      },
    },
  })

  //------------------------------------------------------------------------------------
  // Navigation Event Handlers
  //------------------------------------------------------------------------------------
  eventBus.on('scroll-to:nav-item', ({ id }) => sidebarState.handleSelectItem(id))

  //------------------------------------------------------------------------------------
  // UI Related Event Handlers
  //------------------------------------------------------------------------------------
  eventBus.on('ui:toggle:sidebar', () => (isSidebarOpen.value = !isSidebarOpen.value))
  eventBus.on('ui:close:client-modal', () => modalState.hide())
  eventBus.on('ui:open:client-modal', (payload) => {
    // Every open re-establishes the selection (falling back to empty), so the modal no longer needs
    // to reset it on close.
    const nextRequestBodyCompositionSelection: Record<string, number> =
      payload && 'requestBodyCompositionSelection' in payload && payload.requestBodyCompositionSelection
        ? payload.requestBodyCompositionSelection
        : EMPTY_REQUEST_BODY_COMPOSITION_SELECTION

    // Just open the modal
    if (!payload) {
      requestBodyCompositionSelection.value = nextRequestBodyCompositionSelection
      modalState.show()
      return
    }

    const previousSelectedId = sidebarState.state.selectedItem.value

    // We route to the exact ID
    if ('id' in payload && payload.id) {
      let targetId = payload.id

      // If exampleName is provided, try to find the specific example entry
      if ('exampleName' in payload && payload.exampleName) {
        const operationEntry = sidebarState.state.getEntryById(payload.id)

        // Try to find the specific example in the operation's children
        if (operationEntry && 'children' in operationEntry && operationEntry.children) {
          const exampleEntry = operationEntry.children.find(
            (child: TraversedEntry) => child.type === 'example' && child.name === payload.exampleName,
          )
          if (exampleEntry) {
            targetId = exampleEntry.id
          }
        }
      }

      sidebarState.handleSelectItem(targetId)
    }
    // We must find the ID first from the entries
    else if ('method' in payload && 'path' in payload) {
      const activeDoc = store.workspace.activeDocument
      sidebarState.handleSelectItem(
        sidebarState.getEntryByLocation({
          document: isOpenApiDocument(activeDoc) ? (activeDoc['x-scalar-navigation']?.id ?? '') : '',
          path: payload.path,
          method: payload.method,
          example: payload.exampleName,
        })?.id ?? '',
      )
    }

    // Apply the selection after routing so the request body compares it with the selection used
    // for this operation, rather than briefly resetting the operation that was previously open.
    // Reopening the entry already on screen routes nowhere, so a new selection would read as a
    // manual branch switch and discard the edited body.
    const isReopeningVisibleEntry = modalState.open && sidebarState.state.selectedItem.value === previousSelectedId

    if (!isReopeningVisibleEntry) {
      requestBodyCompositionSelection.value = nextRequestBodyCompositionSelection
    }

    modalState.show()
  })
}
